import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Sparkles,
  Search,
  Heart,
  Trash2,
  Download,
  Copy,
  Wand2,
  Loader2,
  X,
  Grid3X3,
  LayoutList,
  EyeOff,
  Globe,
  ImageIcon,
  ImagePlus,
  Paintbrush,
  ArrowRight,
  ZoomIn,
  Video as VideoIcon,
  Clapperboard,
} from 'lucide-react';
import { worksAPI, type WorkItem, type WorkDetail, api } from '../services/api';

/** modelId → 模型展示名映射 */
const imageNameModelMap = new Map<string, string>();
import { resolveBrowserMediaUrl } from '../utils/storageUrl';
import { MyVideosPanel } from '../baselines/video/components/MyVideosPanel';
import { LazyImage } from '../components/LazyImage';
import { WorksDirectorSection } from '../apps/director/components/WorksDirectorSection';
import { useSiteName } from '../platform/useSiteName';

// 复用模板广场的分类体系
const PUBLISH_CATEGORIES = [
  { value: 'general', label: '综合' },
  { value: 'ecommerce', label: '电商与产品' },
  { value: 'poster', label: '品牌海报' },
  { value: 'typography', label: '创意字体' },
  { value: 'infographic', label: '知识科普' },
  { value: 'ui', label: 'UI界面' },
  { value: 'photography', label: '真实摄影' },
  { value: 'character', label: '角色一致性' },
  { value: 'edit', label: '图片编辑' },
  { value: 'art', label: '艺术创作' },
  { value: 'portrait', label: '人像' },
  { value: 'landscape', label: '风景' },
  { value: 'anime', label: '动漫' },
  { value: 'architecture', label: '建筑' },
  { value: 'food', label: '美食' },
  { value: 'scifi', label: '科幻' },
  { value: 'nature', label: '自然' },
  { value: 'other', label: '其他' },
];

const FILTERS = [
  { key: 'all', label: '全部' },
  { key: 'completed', label: '已完成' },
  { key: 'failed', label: '失败' },
  { key: 'favorite', label: '收藏' },
  { key: 'public', label: '已公开' },
];

const MODES: Record<string, string> = {
  text2img: '文生图',
  img2img: '参考图',
  inpaint: '局部重绘',
};

export default function WorksPage() {
  const navigate = useNavigate();
  const siteName = useSiteName();
  const [mediaType, setMediaType] = useState<'image' | 'video' | 'director'>('image');
  const [works, setWorks] = useState<WorkItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [selectedWork, setSelectedWork] = useState<WorkDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [publishForm, setPublishForm] = useState({ publicTitle: '', publicDescription: '', publicCategory: 'general', isPromptPublic: true });
  const [analyzing, setAnalyzing] = useState(false);

  // 加载模型列表构建 modelId→名称 映射，供卡片和详情展示模型名
  useEffect(() => {
    api.getModels().then((res) => {
      for (const m of res.data) imageNameModelMap.set(m.id, m.name);
    }).catch(() => {});
  }, []);

  const loadWorks = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params: Record<string, unknown> = { page, limit: 24 };
      if (activeFilter === 'completed') params.status = 'completed';
      if (activeFilter === 'failed') params.status = 'failed';
      if (activeFilter === 'favorite') params.isFavorite = true;
      if (activeFilter === 'public') params.isPublic = true;
      if (searchQuery) params.search = searchQuery;

      const res = await worksAPI.getWorks(params);
      setWorks(res.data);
      setTotalPages(res.pagination.totalPages);
    } catch (e) {
      setError('加载作品失败');
    } finally {
      setLoading(false);
    }
  }, [activeFilter, searchQuery, page]);

  useEffect(() => {
    loadWorks();
  }, [loadWorks]);

  const handleOpenWork = async (work: WorkItem) => {
    setDetailLoading(true);
    try {
      const detail = await worksAPI.getWork(work.id);
      setSelectedWork(detail);
    } catch {
      setError('加载作品详情失败');
    } finally {
      setDetailLoading(false);
    }
  };

  const handleToggleFavorite = async (workId: string, current: boolean) => {
    try {
      await worksAPI.updateWork(workId, { isFavorite: !current });
      setWorks((prev) =>
        prev.map((w) => (w.id === workId ? { ...w, isFavorite: !current } : w))
      );
      if (selectedWork?.id === workId) {
        setSelectedWork((prev) => (prev ? { ...prev, isFavorite: !current } : prev));
      }
    } catch {
      setError('操作失败');
    }
  };

  const handleHideWork = async (workId: string) => {
    try {
      await worksAPI.hideWork(workId);
      setWorks((prev) => prev.filter((w) => w.id !== workId));
      setSelectedWork(null);
    } catch {
      setError('隐藏失败');
    }
  };

  const handleRegenerate = async (work: WorkItem | WorkDetail) => {
    try {
      await api.generateImage({
        model: work.model,
        prompt: work.prompt,
        size: work.size,
        quality: work.quality,
        mode: work.mode as 'text2img' | 'img2img' | 'inpaint',
        moderation: 'low',
      });

      navigate('/');
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : '再次生成失败';
      setError(msg);
    }
  };

  // 作为参考图进行迭代
  const handleUseAsReference = async (work: WorkItem | WorkDetail) => {
    const imgUrl = getImageUrl(work);
    if (!imgUrl) {
      setError('图片尚未生成完成');
      return;
    }

    // 导航到首页并传递参数
    navigate('/', {
      state: {
        continueIteration: true,
        jobId: work.id,
        imageUrl: imgUrl,
        prompt: work.prompt,
        mode: 'img2img',
      },
    });
  };

  // 局部重绘
  const handleInpaint = async (work: WorkItem | WorkDetail) => {
    const imgUrl = getImageUrl(work);
    if (!imgUrl) {
      setError('图片尚未生成完成');
      return;
    }

    // 导航到首页并传递参数
    navigate('/', {
      state: {
        continueIteration: true,
        jobId: work.id,
        imageUrl: imgUrl,
        prompt: work.prompt,
        mode: 'inpaint',
      },
    });
  };

  const handleUseRefAsReference = (refUrl: string) => {
    navigate('/', {
      state: {
        continueIteration: true,
        jobId: selectedWork?.id || '',
        imageUrl: refUrl,
        prompt: selectedWork?.prompt || '',
        mode: 'img2img',
      },
    });
  };

  const handleInpaintRef = (refUrl: string) => {
    navigate('/', {
      state: {
        continueIteration: true,
        jobId: selectedWork?.id || '',
        imageUrl: refUrl,
        prompt: selectedWork?.prompt || '',
        mode: 'inpaint',
      },
    });
  };

  const handlePublish = async () => {
    if (!selectedWork) return;
    try {
      await worksAPI.publishWork(selectedWork.id, publishForm);
      setShowPublishModal(false);
      setSelectedWork((prev) => (prev ? { ...prev, isPublic: true } : prev));
      setWorks((prev) =>
        prev.map((w) => (w.id === selectedWork.id ? { ...w, isPublic: true } : w))
      );
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : '公开失败';
      setError(msg);
    }
  };

  // AI自动填充作品标题和分类
  const handleAutoFill = async () => {
    if (!selectedWork) return;
    setAnalyzing(true);
    try {
      const result = await worksAPI.analyzeWork(selectedWork.prompt);
      setPublishForm((prev) => ({
        ...prev,
        publicTitle: result.title,
        publicCategory: result.category,
      }));
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : '分析失败';
      setError(msg);
    } finally {
      setAnalyzing(false);
    }
  };

  const handleUnpublish = async (workId: string) => {
    try {
      await worksAPI.unpublishWork(workId);
      setSelectedWork((prev) => (prev ? { ...prev, isPublic: false } : prev));
      setWorks((prev) =>
        prev.map((w) => (w.id === workId ? { ...w, isPublic: false } : w))
      );
    } catch {
      setError('取消公开失败');
    }
  };

  const getImageUrl = (work: WorkItem | WorkDetail) => {
    if (work.resultUrl) return work.resultUrl;
    return null;
  };

  return (
    <div className="min-h-screen bg-light-bg">
      {/* Header */}
      <div className="bg-white border-b border-light-border sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <button onClick={() => navigate('/')} className="flex items-center gap-2 text-light-text hover:text-accent-blue transition-colors">
                <Sparkles className="w-5 h-5" />
                <span className="font-semibold">{siteName}</span>
              </button>
              <span className="text-light-muted">/</span>
              <span className="font-medium text-light-text">我的作品库</span>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setViewMode(viewMode === 'grid' ? 'list' : 'grid')}
                className="p-2 rounded-xl border border-light-border hover:bg-light-hover transition-colors"
              >
                {viewMode === 'grid' ? <LayoutList className="w-4 h-4" /> : <Grid3X3 className="w-4 h-4" />}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* 媒体类型切换：图片库 / 视频库 */}
        <div className="flex items-center gap-2 mb-6">
          <button
            onClick={() => setMediaType('image')}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all ${
              mediaType === 'image'
                ? 'bg-accent-blue text-white shadow-md'
                : 'bg-white text-light-secondary border border-light-border hover:border-accent-blue hover:text-accent-blue'
            }`}
          >
            <ImageIcon className="w-4 h-4" />
            图片库
          </button>
          <button
            onClick={() => setMediaType('video')}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all ${
              mediaType === 'video'
                ? 'bg-accent-blue text-white shadow-md'
                : 'bg-white text-light-secondary border border-light-border hover:border-accent-blue hover:text-accent-blue'
            }`}
          >
            <VideoIcon className="w-4 h-4" />
            视频库
          </button>
          <button
            onClick={() => setMediaType('director')}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all ${
              mediaType === 'director'
                ? 'bg-accent-blue text-white shadow-md'
                : 'bg-white text-light-secondary border border-light-border hover:border-accent-blue hover:text-accent-blue'
            }`}
          >
            <Clapperboard className="w-4 h-4" />
            导演项目
          </button>
        </div>

        {mediaType === 'video' && <MyVideosPanel />}

        {mediaType === 'director' && <WorksDirectorSection />}

        {mediaType === 'image' && (
        <div>
        {/* Search & Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1 max-w-xl">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-light-muted" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="搜索作品..."
              className="w-full pl-12 pr-4 py-3 bg-white border border-light-border rounded-xl text-light-text placeholder-light-muted focus:outline-none focus:border-accent-blue focus:ring-2 focus:ring-accent-blue/20 shadow-sm"
            />
            {searchQuery && (
              <button onClick={() => setSearchQuery('')} className="absolute right-4 top-1/2 -translate-y-1/2 text-light-muted hover:text-light-text">
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          <div className="flex gap-2 overflow-x-auto pb-2">
            {FILTERS.map((f) => (
              <button
                key={f.key}
                onClick={() => { setActiveFilter(f.key); setPage(1); }}
                className={`px-4 py-2 rounded-xl text-sm font-medium whitespace-nowrap transition-all ${
                  activeFilter === f.key
                    ? 'bg-accent-blue text-white shadow-md'
                    : 'bg-white text-light-secondary border border-light-border hover:border-accent-blue hover:text-accent-blue'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm">
            {error}
          </div>
        )}

        {/* Works */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-accent-blue" />
          </div>
        ) : works.length === 0 ? (
          <div className="text-center py-20">
            <ImageIcon className="w-12 h-12 text-light-muted mx-auto mb-4" />
            <p className="text-light-muted mb-4">暂无作品</p>
            <button onClick={() => navigate('/')} className="btn-primary">
              去生成
            </button>
          </div>
        ) : (
          <>
            <div className={viewMode === 'grid' ? 'grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4' : 'flex flex-col gap-3'}>
              {works.map((work) => {
                const imgUrl = getImageUrl(work);
                return (
                  <div
                    key={work.id}
                    className={`group bg-white border border-light-border rounded-2xl overflow-hidden hover:shadow-lg hover:border-accent-blue/30 transition-all ${
                      viewMode === 'list' ? 'flex items-center gap-4 p-4' : ''
                    }`}
                  >
                    {viewMode === 'grid' && (
                      <div
                        onClick={() => handleOpenWork(work)}
                        className="aspect-square bg-light-hover relative overflow-hidden cursor-pointer"
                      >
                        {imgUrl ? (
                          <LazyImage
                            work={work}
                            size="small"
                            alt={work.title || '作品'}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <ImageIcon className="w-8 h-8 text-light-muted" />
                          </div>
                        )}
                        {work.isPublic && (
                          <div className="absolute top-2 left-2 px-2 py-1 bg-accent-blue/80 backdrop-blur-sm text-white text-xs font-medium rounded-lg flex items-center gap-1">
                            <Globe className="w-3 h-3" />
                            公开
                          </div>
                        )}
                        <div className="absolute top-2 right-2 flex gap-1">
                          <button
                            onClick={(e) => { e.stopPropagation(); handleToggleFavorite(work.id, work.isFavorite); }}
                            className="p-2 rounded-xl bg-white/80 backdrop-blur-sm hover:bg-white transition-colors"
                          >
                            <Heart className={`w-4 h-4 ${work.isFavorite ? 'fill-red-500 text-red-500' : 'text-light-muted'}`} />
                          </button>
                        </div>
                      </div>
                    )}
                    <div className={viewMode === 'grid' ? 'p-3' : 'flex-1'}>
                      <div className="flex items-start justify-between gap-2">
                        <div onClick={() => handleOpenWork(work)} className="cursor-pointer flex-1 min-w-0">
                          <h3 className="font-medium text-light-text text-sm line-clamp-1">{work.title || '未命名作品'}</h3>
                          <p className="text-xs text-light-muted mt-0.5 line-clamp-1">{work.prompt}</p>
                        </div>
                        {viewMode === 'list' && (
                          <button onClick={() => handleToggleFavorite(work.id, work.isFavorite)}>
                            <Heart className={`w-4 h-4 ${work.isFavorite ? 'fill-red-500 text-red-500' : 'text-light-muted'}`} />
                          </button>
                        )}
                      </div>
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mt-2 text-xs text-light-muted">
                        <span className="px-1.5 py-0.5 bg-light-hover rounded whitespace-nowrap">{MODES[work.mode] || work.mode}</span>
                        {work.referenceImages && work.referenceImages.length > 0 && (
                          <span className="px-1.5 py-0.5 bg-blue-50 text-blue-600 rounded flex items-center gap-1 whitespace-nowrap">
                            <ImageIcon className="w-3 h-3" />
                            {work.referenceImages.length}图
                          </span>
                        )}
                        {imageNameModelMap.get(work.model) && (
                          <span className="truncate text-slate-400">{imageNameModelMap.get(work.model)}</span>
                        )}
                        <span className="whitespace-nowrap">{work.size}</span>
                        <span className="whitespace-nowrap">{work.quality}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-8">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1}
                  className="px-4 py-2 bg-white border border-light-border rounded-xl text-sm disabled:opacity-40 hover:border-accent-blue transition-colors"
                >
                  上一页
                </button>
                <span className="text-sm text-light-secondary px-3">{page} / {totalPages}</span>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                  className="px-4 py-2 bg-white border border-light-border rounded-xl text-sm disabled:opacity-40 hover:border-accent-blue transition-colors"
                >
                  下一页
                </button>
              </div>
            )}
          </>
        )}
        </div>
        )}
      </div>

      {/* Work Detail Modal */}
      {selectedWork && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setSelectedWork(null)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-y-auto">
            {detailLoading ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="w-8 h-8 animate-spin text-accent-blue" />
              </div>
            ) : (
              <>
                <div className="sticky top-0 bg-white border-b border-light-border px-6 py-4 flex items-center justify-between z-10">
                  <h2 className="text-lg font-semibold text-light-text">{selectedWork.title || '作品详情'}</h2>
                  <button onClick={() => setSelectedWork(null)} className="p-2 hover:bg-light-hover rounded-xl transition-colors">
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <div className="p-6 space-y-6">
                  {/* Image */}
                  <div className="flex justify-center">
                    {(selectedWork.mode === 'img2img' || selectedWork.mode === 'inpaint') && selectedWork.referenceImages && selectedWork.referenceImages.length > 0 ? (
                      <div className="w-full space-y-3">
                        <div className="text-sm font-medium text-light-muted">原始图 → 生成图</div>
                        <div className="flex flex-col md:flex-row items-center gap-3 md:gap-6 justify-center">
                          <div className="relative">
                            <div className="w-52 h-52 md:w-56 md:h-56 bg-light-hover rounded-xl overflow-hidden border-2 border-light-border group/ref relative">
                              <img
                                src={resolveBrowserMediaUrl(selectedWork.referenceImages[0])}
                                alt="原始参考图"
                                className="w-full h-full object-cover"
                              />
                              <div className="absolute inset-0 bg-black/0 group-hover/ref:bg-black/40 transition-colors hidden md:flex items-center justify-center gap-1.5 opacity-0 group-hover/ref:opacity-100">
                                <button
                                  onClick={(e) => { e.stopPropagation(); handleUseRefAsReference(selectedWork.referenceImages![0]); }}
                                  className="px-2 py-1 bg-white/90 hover:bg-white rounded text-[10px] font-medium text-accent-blue transition-colors flex items-center gap-0.5"
                                >
                                  <ImagePlus className="w-3 h-3" />参考
                                </button>
                                <button
                                  onClick={(e) => { e.stopPropagation(); handleInpaintRef(selectedWork.referenceImages![0]); }}
                                  className="px-2 py-1 bg-white/90 hover:bg-white rounded text-[10px] font-medium text-purple-600 transition-colors flex items-center gap-0.5"
                                >
                                  <Paintbrush className="w-3 h-3" />重绘
                                </button>
                                <a
                                  href={selectedWork.referenceImages![0]}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={(e) => { e.stopPropagation(); }}
                                  className="px-2 py-1 bg-white/90 hover:bg-white rounded text-[10px] font-medium text-green-600 transition-colors flex items-center gap-0.5"
                                >
                                  <ZoomIn className="w-3 h-3" />查看
                                </a>
                              </div>
                              <div className="absolute bottom-1 right-1 flex md:hidden gap-0.5">
                                <button
                                  onClick={(e) => { e.stopPropagation(); handleUseRefAsReference(selectedWork.referenceImages![0]); }}
                                  className="p-1 bg-black/40 rounded text-white"
                                >
                                  <ImagePlus className="w-3 h-3" />
                                </button>
                                <button
                                  onClick={(e) => { e.stopPropagation(); handleInpaintRef(selectedWork.referenceImages![0]); }}
                                  className="p-1 bg-black/40 rounded text-white"
                                >
                                  <Paintbrush className="w-3 h-3" />
                                </button>
                                <a
                                  href={selectedWork.referenceImages![0]}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={(e) => { e.stopPropagation(); }}
                                  className="p-1 bg-black/40 rounded text-white"
                                >
                                  <ZoomIn className="w-3 h-3" />
                                </a>
                              </div>
                            </div>
                            {selectedWork.referenceImages.length > 1 && (
                              <div className="absolute -top-1.5 -right-1.5 bg-accent-blue text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center">
                                +{selectedWork.referenceImages.length - 1}
                              </div>
                            )}
                            <div className="text-center mt-1 text-[10px] text-light-muted">原始图</div>
                          </div>
                          <div className="flex flex-col items-center gap-1">
                            <ArrowRight className="w-5 h-5 text-accent-blue rotate-90 md:rotate-0" />
                            {selectedWork.mode === 'inpaint' && selectedWork.maskImage && (
                              <img
                                src={resolveBrowserMediaUrl(selectedWork.maskImage)}
                                alt="蒙版"
                                className="w-10 h-10 rounded-lg border border-orange-300 object-cover opacity-80"
                                title="蒙版区域"
                              />
                            )}
                          </div>
                          <div>
                            <div className="w-64 h-64 md:w-72 md:h-72 bg-light-hover rounded-xl overflow-hidden border-2 border-accent-blue/30 group/result relative">
                              {getImageUrl(selectedWork) ? (
                                <img src={resolveBrowserMediaUrl(getImageUrl(selectedWork)) || undefined} alt="生成结果" className="w-full h-full object-contain" />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center">
                                  <ImageIcon className="w-8 h-8 text-light-muted" />
                                </div>
                              )}
                              {getImageUrl(selectedWork) && (
                                <>
                                  <div className="absolute inset-0 bg-black/0 group-hover/result:bg-black/40 transition-colors hidden md:flex items-center justify-center gap-1.5 opacity-0 group-hover/result:opacity-100">
                                    <button
                                      onClick={(e) => { e.stopPropagation(); handleUseRefAsReference(getImageUrl(selectedWork)!); }}
                                      className="px-2 py-1 bg-white/90 hover:bg-white rounded text-[10px] font-medium text-accent-blue transition-colors flex items-center gap-0.5"
                                    >
                                      <ImagePlus className="w-3 h-3" />参考
                                    </button>
                                    <button
                                      onClick={(e) => { e.stopPropagation(); handleInpaintRef(getImageUrl(selectedWork)!); }}
                                      className="px-2 py-1 bg-white/90 hover:bg-white rounded text-[10px] font-medium text-purple-600 transition-colors flex items-center gap-0.5"
                                    >
                                      <Paintbrush className="w-3 h-3" />重绘
                                    </button>
                                    <a
                                      href={getImageUrl(selectedWork) || undefined}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      onClick={(e) => { e.stopPropagation(); }}
                                      className="px-2 py-1 bg-white/90 hover:bg-white rounded text-[10px] font-medium text-green-600 transition-colors flex items-center gap-0.5"
                                    >
                                      <ZoomIn className="w-3 h-3" />查看
                                    </a>
                                  </div>
                                  <div className="absolute bottom-1 right-1 flex md:hidden gap-0.5">
                                    <button
                                      onClick={(e) => { e.stopPropagation(); handleUseRefAsReference(getImageUrl(selectedWork)!); }}
                                      className="p-1 bg-black/40 rounded text-white"
                                    >
                                      <ImagePlus className="w-3 h-3" />
                                    </button>
                                    <button
                                      onClick={(e) => { e.stopPropagation(); handleInpaintRef(getImageUrl(selectedWork)!); }}
                                      className="p-1 bg-black/40 rounded text-white"
                                    >
                                      <Paintbrush className="w-3 h-3" />
                                    </button>
                                    <a
                                      href={getImageUrl(selectedWork) || undefined}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      onClick={(e) => { e.stopPropagation(); }}
                                      className="p-1 bg-black/40 rounded text-white"
                                    >
                                      <ZoomIn className="w-3 h-3" />
                                    </a>
                                  </div>
                                </>
                              )}
                            </div>
                            <div className="text-center mt-1 text-[10px] text-light-muted">生成结果</div>
                          </div>
                        </div>
                        {selectedWork.referenceImages.length > 1 && (
                          <div className="flex gap-2 justify-center flex-wrap pt-1">
                            {selectedWork.referenceImages.map((ref, idx) => (
                              <div key={idx} className="relative group/thumb">
                                <img
                                  src={resolveBrowserMediaUrl(ref)}
                                  alt={`参考图 ${idx + 1}`}
                                  className="w-14 h-14 rounded-lg border border-light-border object-cover hover:scale-105 transition-transform cursor-pointer"
                                />
                                <div className="absolute inset-0 bg-black/0 group-hover/thumb:bg-black/50 transition-colors rounded-lg hidden md:flex items-center justify-center gap-0.5 opacity-0 group-hover/thumb:opacity-100">
                                  <button
                                    onClick={(e) => { e.stopPropagation(); handleUseRefAsReference(ref); }}
                                    className="p-1 bg-white/90 hover:bg-white rounded text-[9px] font-medium text-accent-blue transition-colors"
                                  >
                                    <ImagePlus className="w-2.5 h-2.5" />
                                  </button>
                                  <button
                                    onClick={(e) => { e.stopPropagation(); handleInpaintRef(ref); }}
                                    className="p-1 bg-white/90 hover:bg-white rounded text-[9px] font-medium text-purple-600 transition-colors"
                                  >
                                    <Paintbrush className="w-2.5 h-2.5" />
                                  </button>
                                </div>
                                <div className="absolute bottom-0.5 right-0.5 flex md:hidden gap-0.5">
                                  <button
                                    onClick={(e) => { e.stopPropagation(); handleUseRefAsReference(ref); }}
                                    className="p-0.5 bg-black/40 rounded text-white"
                                  >
                                    <ImagePlus className="w-2 h-2" />
                                  </button>
                                  <button
                                    onClick={(e) => { e.stopPropagation(); handleInpaintRef(ref); }}
                                    className="p-0.5 bg-black/40 rounded text-white"
                                  >
                                    <Paintbrush className="w-2 h-2" />
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="max-w-lg w-full aspect-square bg-light-hover rounded-2xl overflow-hidden group/result relative">
                        {getImageUrl(selectedWork) ? (
                          <img src={resolveBrowserMediaUrl(getImageUrl(selectedWork)) || undefined} alt="作品" className="w-full h-full object-contain" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <ImageIcon className="w-12 h-12 text-light-muted" />
                          </div>
                        )}
                        {getImageUrl(selectedWork) && (
                          <>
                            <div className="absolute inset-0 bg-black/0 group-hover/result:bg-black/40 transition-colors hidden md:flex items-center justify-center gap-2 opacity-0 group-hover/result:opacity-100">
                              <button
                                onClick={(e) => { e.stopPropagation(); handleUseRefAsReference(getImageUrl(selectedWork)!); }}
                                className="px-3 py-1.5 bg-white/90 hover:bg-white rounded-lg text-xs font-medium text-accent-blue transition-colors flex items-center gap-1"
                              >
                                <ImagePlus className="w-3.5 h-3.5" />作为参考图
                              </button>
                              <button
                                onClick={(e) => { e.stopPropagation(); handleInpaintRef(getImageUrl(selectedWork)!); }}
                                className="px-3 py-1.5 bg-white/90 hover:bg-white rounded-lg text-xs font-medium text-purple-600 transition-colors flex items-center gap-1"
                              >
                                <Paintbrush className="w-3.5 h-3.5" />局部重绘
                              </button>
                              <a
                                href={getImageUrl(selectedWork) || undefined}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => { e.stopPropagation(); }}
                                className="px-3 py-1.5 bg-white/90 hover:bg-white rounded-lg text-xs font-medium text-green-600 transition-colors flex items-center gap-1"
                              >
                                <ZoomIn className="w-3.5 h-3.5" />查看原图
                              </a>
                            </div>
                            <div className="absolute bottom-2 right-2 flex md:hidden gap-1.5">
                              <button
                                onClick={(e) => { e.stopPropagation(); handleUseRefAsReference(getImageUrl(selectedWork)!); }}
                                className="p-1.5 bg-black/40 rounded-lg text-white"
                              >
                                <ImagePlus className="w-4 h-4" />
                              </button>
                              <button
                                onClick={(e) => { e.stopPropagation(); handleInpaintRef(getImageUrl(selectedWork)!); }}
                                className="p-1.5 bg-black/40 rounded-lg text-white"
                              >
                                <Paintbrush className="w-4 h-4" />
                              </button>
                              <a
                                href={getImageUrl(selectedWork) || undefined}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => { e.stopPropagation(); }}
                                className="p-1.5 bg-black/40 rounded-lg text-white"
                              >
                                <ZoomIn className="w-4 h-4" />
                              </a>
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Info */}
                  <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 text-sm">
                    <div className="p-3 bg-light-hover rounded-xl">
                      <div className="text-light-muted text-xs mb-1">模式</div>
                      <div className="font-medium">{MODES[selectedWork.mode] || selectedWork.mode}</div>
                    </div>
                    <div className="p-3 bg-light-hover rounded-xl">
                      <div className="text-light-muted text-xs mb-1">模型</div>
                      <div className="font-medium">{imageNameModelMap.get(selectedWork.model) || selectedWork.model}</div>
                    </div>
                    <div className="p-3 bg-light-hover rounded-xl">
                      <div className="text-light-muted text-xs mb-1">尺寸</div>
                      <div className="font-medium">{selectedWork.size}</div>
                    </div>
                    <div className="p-3 bg-light-hover rounded-xl">
                      <div className="text-light-muted text-xs mb-1">质量</div>
                      <div className="font-medium">{selectedWork.quality}</div>
                    </div>
                    <div className="p-3 bg-light-hover rounded-xl">
                      <div className="text-light-muted text-xs mb-1">来源</div>
                      <div className="font-medium">
                        {selectedWork.source === 'api' ? (
                          <span className="text-purple-600">API</span>
                        ) : (
                          <span className="text-blue-600">网页</span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Prompt */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <h3 className="font-medium text-light-text">提示词</h3>
                      <button
                        onClick={() => { navigator.clipboard.writeText(selectedWork.prompt); }}
                        className="text-xs text-accent-blue hover:underline flex items-center gap-1"
                      >
                        <Copy className="w-3 h-3" /> 复制
                      </button>
                    </div>
                    <div className="p-4 bg-light-hover rounded-xl text-sm text-light-text whitespace-pre-wrap">
                      {selectedWork.prompt}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex flex-wrap items-center gap-2 pt-2">
                    <button
                      onClick={() => handleRegenerate(selectedWork)}
                      className="btn-primary flex items-center gap-2"
                    >
                      <Wand2 className="w-4 h-4" />
                      再次生成
                    </button>
                    {getImageUrl(selectedWork) && (
                      <>
                        <button
                          onClick={() => handleUseAsReference(selectedWork)}
                          className="px-4 py-2.5 border border-light-border rounded-xl text-sm font-medium hover:bg-light-hover transition-colors flex items-center gap-2"
                        >
                          <ImagePlus className="w-4 h-4" />
                          作为参考图
                        </button>
                        <button
                          onClick={() => handleInpaint(selectedWork)}
                          className="px-4 py-2.5 border border-light-border rounded-xl text-sm font-medium hover:bg-light-hover transition-colors flex items-center gap-2"
                        >
                          <Paintbrush className="w-4 h-4" />
                          局部重绘
                        </button>
                        <a
                          href={getImageUrl(selectedWork) || undefined}
                          download
                          className="px-4 py-2.5 border border-light-border rounded-xl text-sm font-medium hover:bg-light-hover transition-colors flex items-center gap-2"
                        >
                          <Download className="w-4 h-4" />
                          下载
                        </a>
                      </>
                    )}
                    <button
                      onClick={() => handleToggleFavorite(selectedWork.id, selectedWork.isFavorite)}
                      className={`px-4 py-2.5 border rounded-xl text-sm font-medium transition-colors flex items-center gap-2 ${
                        selectedWork.isFavorite
                          ? 'border-red-200 bg-red-50 text-red-600 hover:bg-red-100'
                          : 'border-light-border hover:bg-light-hover'
                      }`}
                    >
                      <Heart className={`w-4 h-4 ${selectedWork.isFavorite ? 'fill-red-500' : ''}`} />
                      {selectedWork.isFavorite ? '已收藏' : '收藏'}
                    </button>
                    {selectedWork.isPublic ? (
                      <button
                        onClick={() => handleUnpublish(selectedWork.id)}
                        className="px-4 py-2.5 border border-light-border rounded-xl text-sm font-medium hover:bg-light-hover transition-colors flex items-center gap-2"
                      >
                        <EyeOff className="w-4 h-4" />
                        取消公开
                      </button>
                    ) : (
                      <button
                        onClick={() => {
                          setPublishForm({
                            publicTitle: selectedWork.title || '未命名作品',
                            publicDescription: selectedWork.description || '',
                            publicCategory: 'general',
                            isPromptPublic: true,
                          });
                          setShowPublishModal(true);
                        }}
                        className="px-4 py-2.5 border border-light-border rounded-xl text-sm font-medium hover:bg-light-hover transition-colors flex items-center gap-2"
                      >
                        <Globe className="w-4 h-4" />
                        公开到广场
                      </button>
                    )}
                    <button
                      onClick={() => handleHideWork(selectedWork.id)}
                      className="px-4 py-2.5 border border-light-border rounded-xl text-sm font-medium hover:bg-red-50 hover:text-red-600 hover:border-red-200 transition-colors flex items-center gap-2 ml-auto"
                    >
                      <Trash2 className="w-4 h-4" />
                      删除
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Publish Modal */}
      {showPublishModal && selectedWork && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowPublishModal(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="px-6 py-4 border-b border-light-border flex items-center justify-between">
              <h3 className="font-semibold text-light-text">公开作品</h3>
              <button onClick={() => setShowPublishModal(false)} className="p-2 hover:bg-light-hover rounded-xl">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-sm text-light-secondary">公开标题</label>
                  <button
                    type="button"
                    onClick={handleAutoFill}
                    disabled={analyzing || !selectedWork?.prompt}
                    className="text-xs flex items-center gap-1 text-accent-blue hover:text-accent-blue/80 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {analyzing ? (
                      <>
                        <Loader2 className="w-3 h-3 animate-spin" />
                        分析中...
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-3 h-3" />
                        AI自动填充
                      </>
                    )}
                  </button>
                </div>
                <input
                  type="text"
                  value={publishForm.publicTitle}
                  onChange={(e) => setPublishForm((p) => ({ ...p, publicTitle: e.target.value }))}
                  className="w-full px-4 py-3 bg-white border border-light-border rounded-xl text-light-text focus:outline-none focus:border-accent-blue focus:ring-2 focus:ring-accent-blue/20"
                />
              </div>
              <div>
                <label className="text-sm text-light-secondary mb-1.5 block">分类</label>
                <select
                  value={publishForm.publicCategory}
                  onChange={(e) => setPublishForm((p) => ({ ...p, publicCategory: e.target.value }))}
                  className="w-full px-4 py-3 bg-white border border-light-border rounded-xl text-light-text focus:outline-none focus:border-accent-blue focus:ring-2 focus:ring-accent-blue/20"
                >
                  {PUBLISH_CATEGORIES.map((cat) => (
                    <option key={cat.value} value={cat.value}>{cat.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-sm text-light-secondary mb-1.5 block">公开描述</label>
                <textarea
                  value={publishForm.publicDescription}
                  onChange={(e) => setPublishForm((p) => ({ ...p, publicDescription: e.target.value }))}
                  rows={3}
                  className="w-full px-4 py-3 bg-white border border-light-border rounded-xl text-light-text resize-none focus:outline-none focus:border-accent-blue focus:ring-2 focus:ring-accent-blue/20"
                />
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="promptPublic"
                  checked={publishForm.isPromptPublic}
                  onChange={(e) => setPublishForm((p) => ({ ...p, isPromptPublic: e.target.checked }))}
                  className="rounded border-light-border"
                />
                <label htmlFor="promptPublic" className="text-sm text-light-secondary">同时公开提示词</label>
              </div>
              <div className="flex gap-3 pt-2">
                <button onClick={() => setShowPublishModal(false)} className="flex-1 px-4 py-2.5 border border-light-border rounded-xl text-sm font-medium hover:bg-light-hover transition-colors">
                  取消
                </button>
                <button onClick={handlePublish} className="flex-1 btn-primary">
                  确认公开
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
